import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Heart, Share, FileText, Clock, User, Sparkles, Scale, Briefcase, MessageSquare } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createDocumentFromChat, cleanMarkdownForDisplay } from '@/services/documentTransferService';
import { PdfDownloadButton } from '@/components/ui/pdf-download-button';
import { PrintButton } from '@/components/ui/print-button';
import { LegalEditButton } from './LegalEditButton';
import { JurisprudenciaDetailDialog } from '@/components/JurisprudenciaDetailDialog';

interface ChatSource {
  resource_type: string;
  resource_id: string;
  title: string;
  snippet: string;
}

interface MessageBubbleProps {
  role: "user" | "assistant";
  content: string;
  id?: string;
  created_at?: string;
  isStreaming?: boolean;
  sources?: ChatSource[];
}

const MessageBubble = ({ role, content, id, created_at, isStreaming, sources }: MessageBubbleProps) => {
  const isUser = role === "user";
  const [liked, setLiked] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [selectedJurisprudenciaId, setSelectedJurisprudenciaId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleLike = () => {
    setLiked(!liked);
    toast({
      title: liked ? "Curtida removida" : "Resposta curtida!",
      description: liked ? "" : "Obrigado pelo feedback!",
      duration: 2000,
    });
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Resposta Justiçai Legal",
          text: content,
        });
      } else {
        await navigator.clipboard.writeText(content);
        toast({
          title: "Texto copiado!",
          description: "O conteúdo foi copiado para a área de transferência.",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    }
  };

  const handleTransformToDocument = async () => {
    if (!user) {
      toast({
        title: "Erro",
        description: "Você precisa estar autenticado para criar documentos.",
        variant: "destructive",
      });
      return;
    }

    setIsTransferring(true);

    try {
      // Limpar markdown do conteúdo
      const cleanedContent = cleanMarkdownForDisplay(content);
      
      // Criar documento no banco
      const documentId = await createDocumentFromChat(cleanedContent, user.id);

      if (documentId) {
        toast({
          title: "Documento criado!",
          description: "O conteúdo foi transferido para a área de documentos.",
          duration: 4000,
        });

        // Navegar para o documento criado
        navigate(`/documentos/${documentId}`);
      } else {
        throw new Error('Falha ao criar documento');
      }
    } catch (error) {
      console.error('Erro ao transferir para documento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o documento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'long',
    });
  };

  return (
    <div className={`flex items-start gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-1">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
      )}
      <div
        className={`${
          isUser
            ? "max-w-[75%] rounded-2xl bg-muted/80 text-foreground px-4 py-3"
            : "max-w-[85%] rounded-2xl bg-accent/50 text-foreground px-4 py-3 group"
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}

        {/* Sources / References */}
        {!isUser && sources && sources.length > 0 && (
          <div className="mt-2 pt-2 border-t border-secondary-foreground/10">
            <p className="text-xs text-muted-foreground mb-1.5">📎 Fontes utilizadas:</p>
            <div className="flex flex-wrap gap-1.5">
              {sources.map((source, i) => {
                const getIcon = () => {
                  switch (source.resource_type) {
                    case 'JURISPRUDENCIA': return <Scale className="h-3 w-3" />;
                    case 'CASO': case 'CASO_DOCUMENT': return <Briefcase className="h-3 w-3" />;
                    case 'LEGAL_CHAT': return <MessageSquare className="h-3 w-3" />;
                    default: return <FileText className="h-3 w-3" />;
                  }
                };
                const handleSourceClick = () => {
                  if (source.resource_type === 'JURISPRUDENCIA') {
                    setSelectedJurisprudenciaId(source.resource_id);
                  } else {
                    const path = (() => {
                      switch (source.resource_type) {
                        case 'CASO': case 'CASO_DOCUMENT': return `/casos/${source.resource_id}`;
                        case 'LEGAL_CHAT': return `/chat-juridico/${source.resource_id}`;
                        default: return '#';
                      }
                    })();
                    if (path !== '#') navigate(path);
                  }
                };
                return (
                  <button
                    key={i}
                    onClick={handleSourceClick}
                    className="inline-flex items-center gap-1 text-xs bg-primary/10 hover:bg-primary/20 text-primary px-2 py-1 rounded-full transition-colors"
                  >
                    {getIcon()}
                    <span className="truncate max-w-[180px]">{source.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isUser && id && !isStreaming && (
          <TooltipProvider>
            <div className="flex justify-end gap-1 md:gap-2 mt-1 md:mt-2 pt-1 border-t border-secondary-foreground/10">
              <div className="flex items-center gap-1 mr-auto text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{formatTimestamp(created_at)}</span>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleLike}
                    aria-label="Curtir"
                  >
                    <Heart className={`h-3 w-3 md:h-4 md:w-4 ${liked ? 'fill-red-500 text-red-500' : 'text-secondary-foreground/70'}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Curtir esta resposta</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleShare}
                    aria-label="Compartilhar"
                  >
                    <Share className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Compartilhar ou copiar texto</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-5 w-5 md:h-6 md:w-6" 
                    onClick={handleTransformToDocument}
                    disabled={isTransferring}
                    aria-label="Transformar em documento"
                  >
                    {isTransferring ? (
                      <div className="animate-spin h-3 w-3 md:h-4 md:w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <FileText className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Transformar em documento</p>
                </TooltipContent>
              </Tooltip>

              <LegalEditButton 
                messageId={id}
                currentContent={content}
              />

              <PdfDownloadButton 
                content={content} 
                messageId={id} 
              />

              <PrintButton 
                content={content} 
                messageId={id} 
              />
            </div>
          </TooltipProvider>
        )}
      </div>
      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center mt-1">
          <User className="h-4 w-4 text-primary-foreground" />
        </div>
      )}
      <JurisprudenciaDetailDialog
        jurisprudenciaId={selectedJurisprudenciaId}
        open={!!selectedJurisprudenciaId}
        onOpenChange={(open) => { if (!open) setSelectedJurisprudenciaId(null); }}
        chatMode
      />
    </div>
  );
};

export default MessageBubble;
